import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, STATUS_CONFIG, CLIENT_STATUS_CONFIG } from "@/pages/OrdersPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { isWarehouseView } from "@/utils/permissions";
import {
  Menu,
  ArrowLeft,
  Save,
  Edit3,
  Send,
  Trash2,
  RotateCcw,
  FileDown,
  MapPin,
  Plus,
} from "lucide-react";

const STATUS_LABELS = {
  da_cliente: "Da Cliente",
  da_trasferire: "Da Trasferire",
  ricevuto: "Ricevuto",
  fatturato: "Fatturato",
  // Legacy (display-only)
  in_preparazione: "Ricevuto",
  fatturazione: "Ricevuto",
  chiuso: "Fatturato",
};

// Etichette friendly cronologia per il cliente
const CLIENT_STATUS_LABELS = {
  da_cliente: "Inviato",
  da_trasferire: "In elaborazione",
  ricevuto: "Confermato",
  in_preparazione: "Confermato",
  fatturazione: "Confermato",
  fatturato: "Completato",
  chiuso: "Completato",
};

export default function OrderDetailView() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItems, setEditingItems] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [savingItems, setSavingItems] = useState(false);
  // Add-product overlay (used in edit mode to insert new lines)
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchQ, setProductSearchQ] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Guard "modifiche non salvate": in edit mode, se editedItems != order.items → dirty
  const isDirty = editingItems && order
    ? JSON.stringify(editedItems) !== JSON.stringify(order.items)
    : false;
  useUnsavedChangesGuard(isDirty);

  const isWarehouse = isWarehouseView(user);
  const isAdmin = user?.role === "admin";
  const isAgent = user?.role === "agent";
  const isClient = user?.role === "client";
  // Cliente contrattualizzato (sconti_fissi): TUTTI i prezzi nascosti anche negli ordini
  const hidePrices = isClient && !!user?.sconti_fissi;
  const basePath = isWarehouse
    ? "/warehouse"
    : isAdmin
    ? "/admin"
    : isClient
    ? "/client"
    : "/agent";

  const status = order?.status;
  const isFatturato = status === "fatturato" || status === "chiuso";
  const isRicevuto = status === "ricevuto" || status === "in_preparazione" || status === "fatturazione";
  const isDaTrasferire = status === "da_trasferire";
  const isDaCliente = status === "da_cliente";
  const isMyOrder = isAgent && order?.created_by === user?.id;

  // Permessi edit articoli:
  //  - Admin: sempre
  //  - Magazziniere: solo RICEVUTO
  //  - Agent: i propri ordini DA_TRASFERIRE oppure RICEVUTO se non ancora in un giro
  const inDeliveryRound = !!order?.delivery_round_id;
  const canEditItems =
    isAdmin ||
    (isWarehouse && isRicevuto) ||
    (isAgent && isMyOrder && (isDaTrasferire || (isRicevuto && !inDeliveryRound)));

  const canTransfer = (isAdmin || (isAgent && isMyOrder)) && isDaTrasferire;
  const canAccept = (isAdmin || isAgent) && isDaCliente;
  const canReopen = isAdmin && isFatturato;
  const canDelete = isAdmin && !order?.delivery_round_id && !isFatturato;
  // PDF nascosto al cliente contrattualizzato (conterrebbe i prezzi)
  const canDownloadPdf = !!order && !hidePrices;
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Marginalità ordine (solo admin)
  const [margin, setMargin] = useState(null);
  const [showMargin, setShowMargin] = useState(false);
  useEffect(() => {
    if (!isAdmin || !orderId) return;
    authAxios.get(`/orders/${orderId}/margin`)
      .then((r) => setMargin(r.data))
      .catch(() => setMargin(null));
  }, [isAdmin, orderId, authAxios, order?.items?.length]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await authAxios.get(`/orders/${orderId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ordine_${order?.numero || "ORD"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const [archiveItemsLoading, setArchiveItemsLoading] = useState(false);
  const [archiveItemsError, setArchiveItemsError] = useState(null);

  const loadArchiveItems = useCallback(async () => {
    setArchiveItemsLoading(true);
    setArchiveItemsError(null);
    try {
      const res = await authAxios.get(`/orders/${orderId}/archive-items`);
      const items = res.data?.items || [];
      setOrder((prev) => (prev ? { ...prev, items } : prev));
      setEditedItems(items.map((it) => ({ ...it })));
    } catch (err) {
      setArchiveItemsError(err.response?.data?.detail || "Impossibile recuperare gli articoli dall'archivio");
    } finally {
      setArchiveItemsLoading(false);
    }
  }, [authAxios, orderId]);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await authAxios.get(`/orders/${orderId}`);
      setOrder(res.data);
      setEditedItems(res.data.items.map((it) => ({ ...it })));
      // Se l'ordine è archiviato e gli items sono vuoti, prova a ricaricarli dall'archivio Dropbox
      if (res.data?.archived && (!res.data.items || res.data.items.length === 0)) {
        // Fire-and-forget: il loader appare nella card Articoli
        loadArchiveItems();
      }
    } catch {
      toast.error("Ordine non trovato");
      navigate(`${basePath}/orders`);
    } finally {
      setLoading(false);
    }
  }, [authAxios, orderId, navigate, basePath, loadArchiveItems]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleTransfer = async () => {
    setTransferring(true);
    try {
      const res = await authAxios.put(`/orders/${orderId}/transfer`);
      setOrder(res.data);
      setEditedItems(res.data.items.map((it) => ({ ...it })));
      toast.success("Ordine trasferito con successo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel trasferimento");
    } finally {
      setTransferring(false);
    }
  };

  const [accepting, setAccepting] = useState(false);
  const handleAccept = async () => {
    if (!window.confirm("Accettare l'ordine del cliente? Passerà allo stato 'Ricevuto'.")) return;
    setAccepting(true);
    try {
      const res = await authAxios.put(`/orders/${orderId}/accept`);
      setOrder(res.data);
      setEditedItems(res.data.items.map((it) => ({ ...it })));
      toast.success("Ordine accettato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setAccepting(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm("Riaprire questo ordine? Tornerà allo stato 'Ricevuto' e uscirà dal giro di consegna originale.")) return;
    try {
      const res = await authAxios.put(`/orders/${orderId}/reopen`);
      setOrder(res.data);
      setEditedItems(res.data.items.map((it) => ({ ...it })));
      toast.success("Ordine riaperto");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella riapertura");
    }
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm("Eliminare definitivamente questo ordine?")) return;
    try {
      await authAxios.delete(`/orders/${orderId}`);
      toast.success("Ordine eliminato");
      navigate(`${basePath}/orders`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const updateEditedItem = (idx, field, value) => {
    setEditedItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, [field]: value } : it
      )
    );
  };

  const openAddProduct = async () => {
    setShowAddProduct(true);
    setProductSearchQ("");
    if (allProducts.length === 0) {
      setLoadingProducts(true);
      try {
        const res = await authAxios.get("/products");
        setAllProducts(res.data || []);
      } catch {
        toast.error("Impossibile caricare i prodotti");
      } finally {
        setLoadingProducts(false);
      }
    }
  };

  const addProductToEdited = (p) => {
    if (editedItems.some((it) => it.product_id === p.id)) {
      toast.info(`${p.name} è già nell'ordine`);
      setShowAddProduct(false);
      return;
    }
    const price = Number(p.price_list) || 0;
    setEditedItems((prev) => [
      ...prev,
      {
        product_id: p.id,
        product_name: p.name,
        product_sku: p.sku || "",
        quantity: 1,
        qty_omaggio: 0,
        unit: p.unit_of_measure || "pz",
        price_unit: price,
        sc1: 0, sc2: 0, sc3: 0, sc4: 0,
        iva_percentage: p.iva_percentage || 10,
      },
    ]);
    setShowAddProduct(false);
    setProductSearchQ("");
    toast.success(`${p.name} aggiunto`);
  };

  const handleSaveItems = async () => {
    setSavingItems(true);
    try {
      const payload = {
        items: editedItems.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          product_sku: it.product_sku || "",
          quantity: parseFloat(it.quantity) || 0,
          qty_omaggio: parseFloat(it.qty_omaggio) || 0,
          unit: it.unit || "pz",
          price_unit: parseFloat(it.price_unit) || 0,
          sc1: parseFloat(it.sc1) || 0,
          sc2: parseFloat(it.sc2) || 0,
          sc3: parseFloat(it.sc3) || 0,
          sc4: parseFloat(it.sc4) || 0,
          iva_percentage: it.iva_percentage || 10,
        })),
      };
      const res = await authAxios.put(`/orders/${orderId}/items`, payload);
      setOrder(res.data);
      setEditedItems(res.data.items.map((it) => ({ ...it })));
      setEditingItems(false);
      toast.success("Articoli aggiornati");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingItems(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!order) return null;

  const editSubtotal = editedItems.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.price_unit) || 0), 0);
  const editIva = editedItems.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.price_unit) || 0) * (it.iva_percentage || 10)) / 100, 0);
  const editTotal = editSubtotal + editIva;

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="order-detail-view">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Dettaglio Ordine" />
        {/* Header */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-zinc-400 hover:text-white shrink-0 px-2" onClick={() => navigate(`${basePath}/orders`)}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Ordini</span>
            </Button>
            <div className="flex-1 flex items-center gap-2 min-w-0 flex-wrap">
              <h1 className="font-heading text-lg sm:text-xl font-bold text-white truncate">{order.numero}</h1>
              <StatusBadge status={order.status} forClient={isClient} />
              {!isClient && order.giro_provenienza && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono" data-testid="order-giro-provenienza">
                  Giro {order.giro_provenienza}
                </span>
              )}
              {!isClient && order.geo_lat && order.geo_lng && (
                <a
                  href={`https://www.google.com/maps?q=${order.geo_lat},${order.geo_lng}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="order-geo-link"
                  title={`Posizione di presa dell'ordine${order.geo_accuracy ? ` (precisione ~${Math.round(order.geo_accuracy)} m)` : ""}`}
                  className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors inline-flex items-center gap-1"
                >
                  <MapPin className="w-3 h-3" /> Posizione
                </a>
              )}
              {!isClient && order.delivery_round_letter && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/30 font-mono">
                  Cons. {order.delivery_round_letter}
                </span>
              )}
              {!isClient && order.post_transfer_edits?.length > 0 && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 font-medium"
                  title={`Modificato ${order.post_transfer_edits.length} volta/e dopo il trasferimento — vedi la cronologia in basso`}
                  data-testid="post-transfer-edit-badge"
                >
                  ✏️ Modificato dopo trasferimento
                </span>
              )}
              {!isClient && order.archived && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full bg-zinc-500/15 text-zinc-300 border border-zinc-500/40 font-medium"
                  title={`Ordine archiviato su Dropbox il ${order.archived_at?.slice(0, 10) || ''}. Articoli caricati on-demand.`}
                  data-testid="archived-badge"
                >
                  📦 Archiviato
                </span>
              )}
            </div>
          </div>
          {/* Action buttons - wrap on mobile */}
          {(canTransfer || canAccept || canReopen || canDelete || canDownloadPdf) && (
            <div className="flex flex-wrap gap-2 pl-0 lg:pl-0">
              {canDownloadPdf && (
                <Button
                  data-testid="download-order-pdf-btn"
                  variant="outline"
                  size="sm"
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  <FileDown className="w-3.5 h-3.5 mr-1.5" />
                  {downloadingPdf ? "..." : "PDF"}
                </Button>
              )}
              {canAccept && (
                <Button
                  data-testid="accept-order-btn"
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {accepting ? "..." : "Accetta"}
                </Button>
              )}
              {canTransfer && (
                <Button
                  data-testid="transfer-order-btn"
                  size="sm"
                  className="bg-violet-500 hover:bg-violet-600 text-white font-medium"
                  onClick={handleTransfer}
                  disabled={transferring}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {transferring ? "..." : "Trasferisci"}
                </Button>
              )}
              {canReopen && (
                <Button
                  data-testid="reopen-order-btn"
                  variant="outline"
                  size="sm"
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={handleReopen}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Riapri
                </Button>
              )}
              {canDelete && (
                <Button
                  data-testid="delete-order-btn"
                  variant="outline"
                  size="sm"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={handleDeleteOrder}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Elimina
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-zinc-500 text-xs">Ragione Sociale</p>
                    <p className="text-white">{order.client_name}</p>
                  </div>
                  {order.client_partita_iva && (
                    <div>
                      <p className="text-zinc-500 text-xs">P.IVA</p>
                      <p className="text-white">{order.client_partita_iva}</p>
                    </div>
                  )}
                  {order.client_indirizzo && (
                    <div>
                      <p className="text-zinc-500 text-xs">Indirizzo</p>
                      <p className="text-white">{order.client_indirizzo}</p>
                    </div>
                  )}
                  {order.client_citta && (
                    <div>
                      <p className="text-zinc-500 text-xs">Città</p>
                      <p className="text-white">{order.client_citta}</p>
                    </div>
                  )}
                </div>
                {order.destinazione_nome && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <p className="text-zinc-500 text-xs mb-1">Destinazione merce</p>
                    <p className="text-cyan-400 text-sm font-medium" data-testid="order-detail-destinazione">{order.destinazione_nome}</p>
                    {(order.destinazione_indirizzo || order.destinazione_citta) && (
                      <p className="text-zinc-300 text-xs">
                        {[order.destinazione_indirizzo, order.destinazione_citta].filter(Boolean).join(" - ")}
                      </p>
                    )}
                    {order.cod_destin && (
                      <p className="text-zinc-600 text-[10px] font-mono">cod_destin: {order.cod_destin}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-white text-base">Articoli ({(editingItems ? editedItems : order.items).length})</CardTitle>
                {canEditItems && !editingItems && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-amber-400 hover:text-amber-300"
                    onClick={() => setEditingItems(true)}
                    data-testid="edit-items-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Modifica
                  </Button>
                )}
                {editingItems && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-emerald-500/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200 hover:border-emerald-400"
                      onClick={openAddProduct}
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Articolo
                    </Button>
                    <Button variant="ghost" size="sm" className="text-zinc-400" onClick={() => {
                      setEditingItems(false);
                      setEditedItems(order.items.map((it) => ({ ...it })));
                      setShowAddProduct(false);
                    }}>
                      Annulla
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      onClick={handleSaveItems}
                      disabled={savingItems}
                      data-testid="save-items-btn"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {savingItems ? "Salvataggio..." : "Salva"}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {/* Archive loading state */}
                {order.archived && archiveItemsLoading && (
                  <div className="mb-3 p-3 bg-[#0f0f12] border border-zinc-500/30 rounded-lg flex items-center gap-2 text-zinc-300 text-sm" data-testid="archive-items-loading">
                    <RotateCcw className="w-4 h-4 animate-spin" />
                    Caricamento articoli dall'archivio Dropbox...
                  </div>
                )}
                {order.archived && archiveItemsError && (
                  <div className="mb-3 p-3 bg-[#0f0f12] border border-red-500/30 rounded-lg flex items-center justify-between gap-2 text-red-300 text-sm" data-testid="archive-items-error">
                    <span>⚠️ {archiveItemsError}</span>
                    <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-200" onClick={loadArchiveItems}>
                      Riprova
                    </Button>
                  </div>
                )}
                {order.archived && !archiveItemsLoading && !archiveItemsError && order.items.length === 0 && (
                  <div className="mb-3 p-3 bg-[#0f0f12] border border-zinc-500/30 rounded-lg flex items-center justify-between gap-2 text-zinc-300 text-sm">
                    <span>📦 Ordine archiviato — gli articoli sono su Dropbox.</span>
                    <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300" onClick={loadArchiveItems} data-testid="archive-items-load-btn">
                      Carica articoli
                    </Button>
                  </div>
                )}
                {showAddProduct && (
                  <div className="mb-3 p-3 bg-[#0f0f12] border border-emerald-500/30 rounded-lg" data-testid="add-product-panel">
                    <Input
                      data-testid="add-product-search"
                      autoFocus
                      placeholder="Cerca articolo per nome o SKU…"
                      value={productSearchQ}
                      onChange={(e) => setProductSearchQ(e.target.value)}
                      className="bg-[#18181b] border-white/10 text-white mb-2"
                    />
                    <div className="max-h-64 overflow-y-auto">
                      {loadingProducts ? (
                        <div className="text-xs text-zinc-500 py-3 text-center">Caricamento…</div>
                      ) : (() => {
                        const q = productSearchQ.toLowerCase().trim();
                        if (q.length < 1) {
                          return <div className="text-xs text-zinc-500 py-3 text-center">Digita per cercare ({allProducts.length} articoli)</div>;
                        }
                        const matches = allProducts
                          .filter((p) =>
                            (p.name || "").toLowerCase().includes(q) ||
                            (p.sku || "").toLowerCase().includes(q)
                          )
                          .slice(0, 30);
                        if (matches.length === 0) {
                          return <div className="text-xs text-zinc-500 py-3 text-center">Nessun risultato</div>;
                        }
                        return (
                          <ul className="divide-y divide-white/5">
                            {matches.map((p) => {
                              const already = editedItems.some((it) => it.product_id === p.id);
                              return (
                                <li
                                  key={p.id}
                                  data-testid={`add-product-result-${p.sku || p.id}`}
                                  className={`px-2 py-2 hover:bg-white/5 cursor-pointer flex items-center gap-2 ${already ? "opacity-50" : ""}`}
                                  onClick={() => addProductToEdited(p)}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">{p.name}</div>
                                    <div className="text-[10px] text-zinc-500 flex items-center gap-2">
                                      {p.sku && <span className="font-mono">{p.sku}</span>}
                                      {p.price_list > 0 && <span className="text-amber-300">{Number(p.price_list).toFixed(2).replace(".", ",")}€</span>}
                                      {already && <span className="text-emerald-300">· già nell'ordine</span>}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        );
                      })()}
                    </div>
                    <button
                      type="button"
                      className="mt-2 text-xs text-zinc-500 hover:text-zinc-300"
                      onClick={() => setShowAddProduct(false)}
                    >
                      Chiudi
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-zinc-400 py-2 font-medium text-xs">Prodotto</th>
                        <th className="text-center text-zinc-400 py-2 font-medium w-16 text-xs">Qtà</th>
                        {!hidePrices && <th className="text-right text-zinc-400 py-2 font-medium w-16 text-xs">Prezzo</th>}
                        {!hidePrices && <th className="text-right text-zinc-400 py-2 font-medium w-16 text-xs">Netto</th>}
                        {!hidePrices && <th className="text-right text-zinc-400 py-2 font-medium w-16 text-xs">Tot.</th>}
                        {isAdmin && <th className="text-right text-emerald-500/80 py-2 font-medium w-20 text-xs">Marg.</th>}
                        {editingItems && <th className="w-8"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(editingItems ? editedItems : order.items).map((item, idx) => {
                        const s1 = parseFloat(item.sc1) || 0;
                        const s2 = parseFloat(item.sc2) || 0;
                        const s3 = parseFloat(item.sc3) || 0;
                        const s4 = parseFloat(item.sc4) || 0;
                        const sc = parseFloat(order.sconto_canale) || 0;
                        const qtyOm = parseFloat(item.qty_omaggio) || 0;
                        let lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.price_unit) || 0);
                        for (const d of [s1, s2, s3, s4]) { if (d > 0) lineTotal *= (1 - d / 100); }
                        if (sc > 0) lineTotal *= (1 - sc / 100);
                        lineTotal = Math.round(lineTotal * 100) / 100;
                        // Prezzo netto unitario (listino scontato di Sc1-4 + canale)
                        let netUnit = parseFloat(item.price_unit) || 0;
                        for (const d of [s1, s2, s3, s4]) { if (d > 0) netUnit *= (1 - d / 100); }
                        if (sc > 0) netUnit *= (1 - sc / 100);
                        const hasDiscounts = !hidePrices && (s1 > 0 || s2 > 0 || s3 > 0 || s4 > 0 || sc > 0 || qtyOm > 0 || editingItems);
                        const rowBg = idx % 2 === 1 ? "bg-white/[0.025]" : "";
                        return (
                        <React.Fragment key={item.product_id || idx}>
                        <tr className={`${hasDiscounts ? "border-b-0" : "border-b border-white/5"} ${rowBg}`} data-testid={`detail-item-${idx}`}>
                          <td className="pt-2 pb-0">
                            <p className="text-white text-xs">{item.product_name}</p>
                            {item.product_sku && <p className="text-zinc-500 text-[10px]">{item.product_sku}</p>}
                          </td>
                          <td className="pt-2 pb-0 text-center">
                            {editingItems ? (
                              <Input type="number" value={item.quantity}
                                onChange={(e) => updateEditedItem(idx, "quantity", e.target.value)}
                                className="w-16 text-center bg-[#09090b] border-white/10 text-white h-7 text-xs" step="0.1" />
                            ) : (
                              <div>
                                <p className="text-white text-xs">{item.quantity} {item.unit}</p>
                                {qtyOm > 0 && (
                                  <p className="text-fuchsia-400 text-[10px]">+{qtyOm} om.</p>
                                )}
                              </div>
                            )}
                          </td>
                          {!hidePrices && (
                          <td className="pt-2 pb-0 text-right">
                            {editingItems ? (
                              <Input type="number" value={item.price_unit}
                                onChange={(e) => updateEditedItem(idx, "price_unit", e.target.value)}
                                className="w-16 text-right bg-[#09090b] border-white/10 text-white h-7 text-xs" step="0.01" />
                            ) : (
                              <p className="text-white font-mono text-xs">{parseFloat(item.price_unit)?.toFixed(2)}</p>
                            )}
                          </td>
                          )}
                          {!hidePrices && (
                          <td className="pt-2 pb-0 text-right" data-testid={`netto-${idx}`}>
                            <p className={`font-mono text-xs ${(s1+s2+s3+s4+sc) > 0 ? "text-emerald-400" : "text-zinc-400"}`}>
                              {netUnit.toFixed(3)}
                            </p>
                          </td>
                          )}
                          {!hidePrices && (
                          <td className="pt-2 pb-0 text-right text-white font-mono text-xs font-medium">
                            {lineTotal.toFixed(2)}
                          </td>
                          )}
                          {isAdmin && (() => {
                            const mi = margin?.items?.find((m) => m.product_id === item.product_id);
                            if (!mi || !mi.has_cost) {
                              return <td className="pt-2 pb-0 text-right text-zinc-600 font-mono text-xs">—</td>;
                            }
                            const pctColor = mi.margin_pct >= 30 ? "text-emerald-400" : mi.margin_pct >= 15 ? "text-amber-400" : "text-red-400";
                            return (
                              <td className={`pt-2 pb-0 text-right font-mono text-xs ${pctColor}`} data-testid={`margin-${idx}`}
                                  title={`Costo unit. €${mi.cost_unit.toFixed(3)} · Margine €${mi.margin_eur.toFixed(2)}`}>
                                <div>{mi.margin_pct.toFixed(1)}%</div>
                                <div className="text-[9px] opacity-70">€{mi.margin_eur.toFixed(2)}</div>
                              </td>
                            );
                          })()}
                          {editingItems && (
                            <td className="pt-2 pb-0">
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500 hover:text-red-400"
                                data-testid={`remove-item-${idx}`}
                                onClick={() => setEditedItems((prev) => prev.filter((_, i) => i !== idx))}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </td>
                          )}
                        </tr>
                        {hasDiscounts && (
                          <tr className={`border-b border-white/5 ${rowBg}`}>
                            <td colSpan={(editingItems ? 6 : 5) + (isAdmin ? 1 : 0)} className="pb-2 pt-0.5 px-1">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-emerald-500/70 text-[10px]">Sc%</span>
                                {[s1, s2, s3, s4].map((v, i) => (
                                  <React.Fragment key={`sc${i + 1}`}>
                                    {i > 0 && <span className="text-zinc-600 text-[10px]">+</span>}
                                    <span className={`text-[10px] font-mono px-1 rounded ${v > 0 ? "text-emerald-400 bg-emerald-500/10" : "text-zinc-600"}`}>
                                      {v > 0 ? v : "-"}
                                    </span>
                                  </React.Fragment>
                                ))}
                                {sc > 0 && (
                                  <>
                                    <span className="text-zinc-600 text-[10px]">+</span>
                                    <span className="text-cyan-400 text-[10px] font-mono bg-cyan-500/10 px-1 rounded">C:{sc}</span>
                                  </>
                                )}
                                {editingItems && (
                                  <>
                                    <span className="text-zinc-600 text-[10px] ml-2">|</span>
                                    <span className="text-fuchsia-500/70 text-[10px]">Om.</span>
                                    <Input type="number" value={item.qty_omaggio || 0}
                                      onChange={(e) => updateEditedItem(idx, "qty_omaggio", e.target.value)}
                                      className="w-12 text-center bg-[#09090b] border-fuchsia-500/30 text-fuchsia-400 h-6 text-[10px] px-1" step="1" />
                                  </>
                                )}
                                {!editingItems && qtyOm > 0 && (
                                  <>
                                    <span className="text-zinc-600 text-[10px] ml-2">|</span>
                                    <span className="text-fuchsia-400 text-[10px] font-mono bg-fuchsia-500/10 px-1 rounded">+{qtyOm} om.</span>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {order.note && (
              <Card className="bg-[#18181b] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base">Note</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap">{order.note}</p>
                </CardContent>
              </Card>
            )}

            {/* Post-transfer edits (modifiche fatte dall'agente dopo trasferimento) — nascosto al cliente */}
            {!isClient && order.post_transfer_edits?.length > 0 && (
              <Card className="bg-amber-500/5 border-amber-500/30" data-testid="post-transfer-edits-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-amber-300 text-base flex items-center gap-2">
                    ✏️ Modifiche dopo trasferimento ({order.post_transfer_edits.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {order.post_transfer_edits.map((e, idx) => (
                      <div key={`pte-${e.at}-${idx}`} className="flex items-center gap-3" data-testid={`post-transfer-edit-${idx}`}>
                        <div className="w-2 h-2 rounded-full bg-amber-400" />
                        <div className="flex-1">
                          <span className="text-sm text-white">{e.summary || "Modifica"}</span>
                          {e.user_name && <span className="text-xs text-zinc-400 ml-2">da {e.user_name}</span>}
                        </div>
                        <span className="text-xs text-zinc-500">
                          {new Date(e.at).toLocaleString("it-IT")}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status History — per il cliente mostriamo solo le tappe friendly */}
            {order.status_history?.length > 0 && (() => {
              // Per i clienti collassiamo gli step interni e usiamo etichette friendly,
              // deduplicando le tappe consecutive con la stessa etichetta visibile.
              const labelMap = isClient ? CLIENT_STATUS_LABELS : STATUS_LABELS;
              let history = order.status_history;
              if (isClient) {
                const seen = [];
                for (const h of order.status_history) {
                  const lbl = labelMap[h.status] || h.status;
                  if (seen.length === 0 || (labelMap[seen[seen.length - 1].status] || seen[seen.length - 1].status) !== lbl) {
                    seen.push(h);
                  } else {
                    // mantieni la tappa più recente per timestamp
                    seen[seen.length - 1] = h;
                  }
                }
                history = seen;
              }
              return (
              <Card className="bg-[#18181b] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base">Cronologia Stato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {history.map((h, idx) => (
                      <div key={`${h.status}-${h.at}`} className="flex items-center gap-3" data-testid={`history-${idx}`}>
                        <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[h.status]?.color?.replace("text-", "bg-") || "bg-zinc-500"}`} />
                        <div className="flex-1">
                          <span className="text-sm text-white">{labelMap[h.status] || h.status}</span>
                          {!isClient && h.by_name && <span className="text-xs text-zinc-500 ml-2">da {h.by_name}</span>}
                        </div>
                        <span className="text-xs text-zinc-500">
                          {new Date(h.at).toLocaleString("it-IT")}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              );
            })()}
          </div>

          {/* Right: Summary */}
          <div>
            <Card className="bg-[#18181b] border-white/5 sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Riepilogo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  {!isClient && (
                    <div className="flex justify-between text-zinc-400">
                      <span>Creato da</span>
                      <span className="text-white">{order.created_by_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-zinc-400">
                    <span>Data</span>
                    <span className="text-white">{new Date(order.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <div className="border-t border-white/10 my-2" />
                  {hidePrices ? (
                    <div className="flex justify-between text-zinc-400" data-testid="summary-items-count">
                      <span>Articoli</span>
                      <span className="text-white font-mono">{order.items?.length || 0}</span>
                    </div>
                  ) : (
                  <>
                  {(order.sconto_canale || 0) > 0 && (
                    <div className="flex justify-between text-cyan-400 text-xs">
                      <span>Sc. Canale</span>
                      <span className="font-mono">{order.sconto_canale}%</span>
                    </div>
                  )}
                  <div className="flex justify-between text-zinc-400">
                    <span>Imponibile</span>
                    <span className="text-white font-mono">
                      {editingItems ? editSubtotal.toFixed(2) : order.subtotal?.toFixed(2)} EUR
                    </span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>IVA</span>
                    <span className="text-white font-mono">
                      {editingItems ? editIva.toFixed(2) : order.iva_total?.toFixed(2)} EUR
                    </span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between">
                    <span className="text-white font-medium">Totale</span>
                    <span className="text-amber-400 font-mono font-bold text-lg">
                      {editingItems ? editTotal.toFixed(2) : order.total?.toFixed(2)} EUR
                    </span>
                  </div>
                  </>
                  )}
                  {isAdmin && margin && (
                    <div data-testid="order-margin-summary" className="mt-3 pt-3 border-t border-emerald-700/40">
                      <button
                        type="button"
                        onClick={() => setShowMargin((v) => !v)}
                        className="w-full flex items-center justify-between text-xs uppercase tracking-wide text-emerald-300 hover:text-emerald-200"
                      >
                        <span>📊 Marginalità ordine</span>
                        <span className="font-mono text-[10px] text-zinc-500">{showMargin ? "−" : "+"}</span>
                      </button>
                      <div className="mt-1.5 grid grid-cols-3 gap-1.5 text-[11px]">
                        <div>
                          <div className="text-zinc-500">Ricavi</div>
                          <div className="text-zinc-100 font-mono">€{margin.summary.tot_revenue.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Costo</div>
                          <div className="text-amber-400 font-mono">€{margin.summary.tot_cost.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Margine</div>
                          <div className={`font-mono font-bold ${margin.summary.margin_pct >= 30 ? "text-emerald-400" : margin.summary.margin_pct >= 15 ? "text-amber-400" : "text-red-400"}`}>
                            €{margin.summary.margin_eur.toFixed(2)} <span className="text-[10px]">({margin.summary.margin_pct.toFixed(1)}%)</span>
                          </div>
                        </div>
                      </div>
                      {showMargin && (
                        <div className="mt-2 max-h-80 overflow-y-auto border-t border-zinc-800 pt-2" data-testid="order-margin-detail">
                          <table className="w-full text-[10px]">
                            <thead className="text-zinc-500">
                              <tr>
                                <th className="text-left py-1">Articolo</th>
                                <th className="text-right">Qta</th>
                                <th className="text-right">Costo u.</th>
                                <th className="text-right">Margine</th>
                                <th className="text-right">%</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800">
                              {margin.items.map((it, idx) => {
                                const pctColor = it.margin_pct >= 30 ? "text-emerald-400" : it.margin_pct >= 15 ? "text-amber-400" : "text-red-400";
                                return (
                                  <tr key={it.sku || it.product_id || `margin-${idx}`}>
                                    <td className="py-1 text-zinc-300 truncate max-w-[100px]">{it.name || it.sku || "?"}</td>
                                    <td className="text-right text-zinc-400 font-mono">{it.qta}</td>
                                    <td className="text-right text-amber-400 font-mono">{it.has_cost ? `€${it.cost_unit.toFixed(3)}` : "—"}</td>
                                    <td className="text-right text-emerald-400 font-mono">€{it.margin_eur.toFixed(2)}</td>
                                    <td className={`text-right font-mono ${pctColor}`}>{it.margin_pct.toFixed(1)}%</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {margin.summary.n_items_no_cost > 0 && (
                            <div className="mt-1.5 text-[10px] text-amber-400/80">⚠ {margin.summary.n_items_no_cost} articoli senza costo</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
